import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { History, Search, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/api";
import BulkActionsBar from "@/components/bulk/BulkActionsBar";
import ChangeHistoryPanel from "@/components/bulk/ChangeHistoryPanel";
import EffectivePriceTester from "@/pages/admin/EffectivePriceTester";
import { ProductForm, SkuForm, PricingForm, SchemeForm } from "@/pages/admin/CatalogForms";

const FORMS = {
  products: ProductForm,
  skus: SkuForm,
  pricing: PricingForm,
  schemes: SchemeForm,
};

/**
 * Generic list page for Products / SKUs / Pricing / Schemes.
 * Props tell it which master to show.
 */
export default function CatalogList({
  title, subtitle, masterKey, apiPath, entityType,
  columns, // [{key, label, render?}]
  searchPlaceholder = "Search…",
  extraFilters = null,
  extraToolbar = null,
  serverFilters = {},   // extra params merged into GET
  pageSize = 20,
  showEffectiveTester = false,
  formsMap = null,      // override the local FORMS mapping
  onRowClick = null,
  editForm = null,      // optional Form component that accepts `existing` prop
  bulkActionsRenderer = null, // (rows, selectedIds, setSelectedIds, reload) => node
  enrichItems = null,   // async (items) => any — result is passed to each column's render as second arg
  groupBy = null,       // optional (row) => string | null — rows sharing a
                        // non-null key collapse to one displayed row (the
                        // first one on the page), annotated with `_groupSize`
                        // so a column can show "N linked". Edit still opens
                        // on that representative row; the edit form itself
                        // is responsible for loading/managing its siblings.
}) {
  const FORM_TABLE = formsMap || FORMS;
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkEditId = searchParams.get("edit");
  const deepLinkHandled = useRef(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [busy, setBusy] = useState(true);
  const [historyFor, setHistoryFor] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [selected, setSelected] = useState({}); // id -> true
  const [enrichment, setEnrichment] = useState(null);
  const NewForm = FORM_TABLE[masterKey];
  const EditForm = editForm || NewForm;
  const stableFilters = JSON.stringify(serverFilters || {});

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = { page, page_size: pageSize, ...JSON.parse(stableFilters) };
      if (q) params.q = q;
      const { data } = await api.get(apiPath, { params });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setBusy(false);
    }
  }, [apiPath, page, q, pageSize, stableFilters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [stableFilters, q]);

  // Deep-link support (e.g. from global search): ?q=<code>&edit=<id> opens
  // that row's edit form directly once it shows up in the loaded page.
  useEffect(() => {
    if (deepLinkHandled.current || !deepLinkEditId || items.length === 0) return;
    const row = items.find((r) => r.id === deepLinkEditId);
    if (row) {
      deepLinkHandled.current = true;
      setEditRow(row);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      }, { replace: true });
    }
  }, [items, deepLinkEditId, setSearchParams]);

  // Optional single-batch enrichment (e.g. bulk-resolve FK names for popovers).
  // Fires once per items load — never per row (avoid N+1).
  useEffect(() => {
    let cancelled = false;
    if (!enrichItems || items.length === 0) { setEnrichment(null); return; }
    (async () => {
      try {
        const out = await enrichItems(items);
        if (!cancelled) setEnrichment(out || null);
      } catch { if (!cancelled) setEnrichment(null); }
    })();
    return () => { cancelled = true; };
  }, [items, enrichItems]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const displayItems = useMemo(() => {
    if (!groupBy) return items;
    const byKey = new Map();
    const out = [];
    for (const row of items) {
      const key = groupBy(row);
      if (!key) { out.push(row); continue; }
      const existing = byKey.get(key);
      if (existing) { existing._groupSize += 1; continue; }
      const rep = { ...row, _groupSize: 1 };
      byKey.set(key, rep);
      out.push(rep);
    }
    return out;
  }, [items, groupBy]);

  return (
    <div className="p-6 space-y-4" data-testid={`admin-${masterKey}-page`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-neutral-900">{title}</h1>
          <div className="text-sm text-neutral-500 mt-1">{subtitle}</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {NewForm && (
            <Button
              onClick={() => setShowNew(true)}
              className="h-10 tulsi-gradient text-white font-semibold"
              data-testid={`catalog-${masterKey}-new`}
            >
              <Plus className="h-4 w-4 mr-2" /> New
            </Button>
          )}
          <BulkActionsBar
            masterKey={masterKey} label={title}
            canImport canExport
            onImportDone={() => load()}
          />
          {extraToolbar}
          <Button
            variant="outline"
            onClick={() => setHistoryFor({ all: true })}
            className="h-10"
            data-testid={`catalog-${masterKey}-history-all`}
          >
            <History className="h-4 w-4 mr-2" /> History
          </Button>
        </div>
      </div>

      {showEffectiveTester && <EffectivePriceTester />}

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder={searchPlaceholder}
                className="h-10 pl-9"
                data-testid={`catalog-${masterKey}-search`}
              />
            </div>
            {extraFilters}
            <div className="text-xs text-neutral-500 ml-auto">
              <span data-testid={`catalog-${masterKey}-total`}>{total}</span> total
            </div>
          </div>

          {bulkActionsRenderer && Object.values(selected).some(Boolean) && (
            <div data-testid={`catalog-${masterKey}-bulkbar`}>
              {bulkActionsRenderer(
                items,
                Object.keys(selected).filter((k) => selected[k]),
                (nextIds) => setSelected(Object.fromEntries((nextIds || []).map((id) => [id, true]))),
                load,
              )}
            </div>
          )}

          <div className="rounded-xl border border-neutral-200 overflow-x-auto">
            <Table>
              <TableHeader className="bg-neutral-50">
                <TableRow>
                  {bulkActionsRenderer && (
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Select all"
                        data-testid={`catalog-${masterKey}-select-all`}
                        checked={items.length > 0 && items.every((r) => selected[r.id])}
                        onChange={(e) => {
                          if (e.target.checked)
                            setSelected(Object.fromEntries(items.map((r) => [r.id, true])));
                          else setSelected({});
                        }}
                      />
                    </TableHead>
                  )}
                  {columns.map((c) => (
                    <TableHead key={c.key} className="text-[11px] uppercase tracking-wide">
                      {c.label}
                    </TableHead>
                  ))}
                  {EditForm && <TableHead className="text-right">Edit</TableHead>}
                  <TableHead className="text-right">History</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayItems.map((row) => (
                  <TableRow
                    key={row.id}
                    data-testid={`catalog-${masterKey}-row-${row.id}`}
                    className={onRowClick ? "cursor-pointer hover:bg-neutral-50" : ""}
                    onClick={() => onRowClick && onRowClick(row)}
                  >
                    {bulkActionsRenderer && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!selected[row.id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [row.id]: e.target.checked }))}
                          data-testid={`catalog-${masterKey}-select-${row.id}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((c) => (
                      <TableCell key={c.key} className="text-xs">
                        {c.render ? c.render(row, enrichment, load) : (row[c.key] ?? "—")}
                      </TableCell>
                    ))}
                    {EditForm && (
                      <TableCell className="text-right">
                        <Button
                          size="sm" variant="outline"
                          onClick={(e) => { e.stopPropagation(); setEditRow(row); }}
                          data-testid={`catalog-${masterKey}-edit-${row.id}`}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <Button
                        size="sm" variant="ghost"
                        onClick={(e) => { e.stopPropagation(); setHistoryFor({ id: row.id, name: row.name || row.code || row.sku_code }); }}
                        data-testid={`catalog-${masterKey}-history-${row.id}`}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && !busy && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1 + (EditForm ? 1 : 0) + (bulkActionsRenderer ? 1 : 0)} className="text-center text-sm text-neutral-500 py-8">
                      No {title.toLowerCase()} yet. Use Import to bulk-load.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {total > pageSize && (
            <div className="flex items-center justify-between text-xs text-neutral-600 pt-2">
              <div>
                Page <span className="font-semibold">{page}</span> of {totalPages} —
                showing {(page - 1) * pageSize + 1}
                –{Math.min(page * pageSize, total)} of {total.toLocaleString()}
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setPage(1)}
                        disabled={page === 1} data-testid={`catalog-${masterKey}-page-first`}>First</Button>
                <Button size="sm" variant="outline" onClick={() => setPage(page - 1)}
                        disabled={page === 1} data-testid={`catalog-${masterKey}-page-prev`}>Prev</Button>
                <Button size="sm" variant="outline" onClick={() => setPage(page + 1)}
                        disabled={page >= totalPages} data-testid={`catalog-${masterKey}-page-next`}>Next</Button>
                <Button size="sm" variant="outline" onClick={() => setPage(totalPages)}
                        disabled={page >= totalPages} data-testid={`catalog-${masterKey}-page-last`}>Last</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ChangeHistoryPanel
        open={!!historyFor}
        onOpenChange={(v) => !v && setHistoryFor(null)}
        entityType={entityType}
        entityId={historyFor?.id || null}
        title={historyFor?.all ? `${title} — all changes` : `History: ${historyFor?.name || ""}`}
      />

      {NewForm && (
        <NewForm
          open={showNew}
          onOpenChange={setShowNew}
          onSaved={() => load()}
        />
      )}
      {EditForm && editRow && (
        <EditForm
          open={!!editRow}
          onOpenChange={(v) => !v && setEditRow(null)}
          existing={editRow}
          onSaved={() => { setEditRow(null); load(); }}
        />
      )}
    </div>
  );
}
